import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ExternalLink, 
  Check, 
  FileText, 
  Zap, 
  Shield, 
  Globe,
  Code,
  BarChart
} from 'lucide-react';
import { Container, Section, Badge, Card, Button } from '../components/ui';
import { CTA, PageHero } from '../components/sections';
import { PRODUCTS } from '../constants';

const finextractFeatures = [
  {
    icon: FileText,
    title: 'PDF Statement Parsing',
    description: 'Extract data from credit card statements in PDF format with high accuracy.',
  },
  {
    icon: Zap,
    title: 'Lightning Fast',
    description: 'Process statements in under 2 seconds with our optimized AI engine.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'SOC 2 Type II compliant with end-to-end encryption for all data.',
  },
  {
    icon: Globe,
    title: '50+ Banks Supported',
    description: 'Works with major banks and financial institutions worldwide.',
  },
  {
    icon: Code,
    title: 'RESTful API',
    description: 'Simple, well-documented API that integrates with any tech stack.',
  },
  {
    icon: BarChart,
    title: 'Rich Analytics',
    description: 'Get detailed insights with categorized transactions and spending patterns.',
  },
];

const useCases = [
  {
    title: 'Personal Finance Apps',
    description: 'Enable users to import their credit card transactions automatically.',
  },
  {
    title: 'Accounting Software',
    description: 'Streamline expense tracking and reconciliation for businesses.',
  },
  {
    title: 'Lending Platforms',
    description: 'Verify income and spending patterns for credit decisions.',
  },
  {
    title: 'Expense Management',
    description: 'Automate corporate expense reporting and categorization.',
  },
];

export const Products: React.FC = () => {
  const product = PRODUCTS[0]; // FinExtract

  return (
    <>
      {/* Hero Section */}
      <PageHero
        badge={
          <Badge className="mb-6 bg-accent-500/20 text-accent-300 border border-accent-500/30">
            Our Products
          </Badge>
        }
        title="AI-Powered Solutions for Modern Business"
        subtitle="We build innovative products that solve real business challenges using cutting-edge AI and machine learning technologies."
      />

      {/* FinExtract Hero */}
      <Section background="white" padding="lg">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge variant="accent" className="mb-4">Featured Product</Badge>
              <h2 className="heading-lg text-navy-900 mb-4">
                {product.name}
              </h2>
              <p className="text-xl text-accent-600 mb-4">{product.tagline}</p>
              <p className="text-body text-lg mb-8">
                {product.description}
              </p>

              {/* Key Features List */}
              <ul className="space-y-3 mb-8">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-navy-700">
                    <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-accent-600" />
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTAs */}
              <div className="flex flex-wrap gap-4">
                <a href={product.url} target="_blank" rel="noopener noreferrer">
                  <Button variant="primary" size="lg" rightIcon={<ExternalLink className="w-5 h-5" />}>
                    Try FinExtract Free
                  </Button>
                </a>
                <a href={`${product.url}/docs`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" rightIcon={<ExternalLink className="w-4 h-4" />}>
                    View API Docs
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Visual */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              {/* Code/API Preview */}
              <div className="bg-navy-900 rounded-2xl p-6 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <pre className="text-sm font-mono overflow-x-auto">
                  <code className="text-gray-300">
{`// Parse credit card statement
const response = await fetch(
  'https://api.finextract.istatec.com/parse',
  {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      document: base64EncodedPDF,
      bank: 'auto-detect'
    })
  }
);

const data = await response.json();
console.log(data.transactions);
// [{
//   date: '2024-01-15',
//   merchant: 'Amazon',
//   amount: 125.99,
//   category: 'Shopping'
// }, ...]`}
                  </code>
                </pre>
              </div>

              {/* Floating Stats */}
              <motion.div
                className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                <div className="text-2xl font-bold text-navy-900">99.9%</div>
                <div className="text-sm text-navy-500">Accuracy</div>
              </motion.div>
              
              <motion.div
                className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4"
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
              >
                <div className="text-2xl font-bold text-navy-900">&lt;2s</div>
                <div className="text-sm text-navy-500">Processing Time</div>
              </motion.div>
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Features Grid */}
      <Section background="mesh" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="accent" className="mb-4">Features</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              Everything You Need to Parse Financial Documents
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {finextractFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card hover className="h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy-100 to-accent-50 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-navy-700" />
                  </div>
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-navy-600 text-sm">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Use Cases */}
      <Section background="white" padding="lg">
        <Container>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <Badge variant="primary" className="mb-4">Use Cases</Badge>
            <h2 className="heading-lg text-navy-900 mb-4">
              Built for Fintech Innovation
            </h2>
            <p className="text-body text-lg">
              See how companies are using FinExtract to transform their financial data processing.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-accent-600 font-bold">{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-navy-900 mb-1">
                      {useCase.title}
                    </h3>
                    <p className="text-navy-600 text-sm">{useCase.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Pricing Teaser */}
      <Section background="gray" padding="lg">
        <Container size="md">
          <Card className="text-center bg-gradient-to-br from-navy-900 to-navy-800 text-white">
            <h2 className="text-2xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-navy-200 mb-6 max-w-lg mx-auto">
              Start with our free tier to test the API, then scale as you grow. 
              Enterprise plans available for high-volume needs.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={product.url} target="_blank" rel="noopener noreferrer">
                <Button variant="accent" size="lg">
                  Start Free Trial
                </Button>
              </a>
              <Link to="/contact?inquiry=enterprise">
                <Button 
                  variant="ghost" 
                  size="lg" 
                  className="text-white hover:bg-white/10 border border-white/20"
                >
                  Contact for Enterprise
                </Button>
              </Link>
            </div>
          </Card>
        </Container>
      </Section>

      <CTA />
    </>
  );
};

